// @ts-nocheck

import React from 'react';
import React, {useEffect, useState} from 'react';
import {Dropdown, Badge} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Form from 'react-bootstrap/Form';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import Icon from '@/components/icons/Icon';
import {UserPreferences} from '@/views/settings/stores/user_preferences';
import axios from 'axios';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {useAlert} from '@/components/alerts';

export interface Props {
    eventId: number;
    timelineBlock: any;
    transcriptAvailable: boolean;
    isMono: boolean;
    agentChannelId: number
    waveSurfer: any;
    isTranscriptionTabClicked?: boolean;
    autoScrollScope?: 'event' | 'search' | 'textSamples';
}

const CallTranscript = React.memo<Props>(({
    eventId,
    timelineBlock,
    transcriptAvailable,
    isMono,
    agentChannelId,
    waveSurfer,
    isTranscriptionTabClicked,
    autoScrollScope = 'event',
}) => {

    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const {showError} = useAlert();
    const [optionsData, setOptionsData] = useState(null);
    const [preferences, setPreferences] = useState<UserPreferences | null>(null);
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);

    const getAutoScrollPreferenceKey = () => {
        if (autoScrollScope === 'search') {
            return 'autoScrollSearchView';
        }
        if (autoScrollScope === 'textSamples') {
            return 'autoScrollTextSamples';
        }
        return 'autoScrollEventView';
    };

    const getAutoScrollValue = (rawPreferences: any): boolean => {
        const key = getAutoScrollPreferenceKey();
        if (key === 'autoScrollEventView') {
            return Boolean(rawPreferences?.[key] ?? rawPreferences?.playerAutoScroll);
        }
        return Boolean(rawPreferences?.[key]);
    };

    const loadPreferences = async () => {
        try {
            const response = await axios.get(window.URLS.userPreferencesResource);
            const raw = response?.data;
            const parsedAutoScroll = getAutoScrollValue(raw);
            setPreferences(raw);
            setAutoScrollEnabled(parsedAutoScroll);
        } catch (error) {
            console.error(i18n.t('jt_error_fetching_user_preferences.text'), error);
        }
    };

    // Fetch preferences once on mount to initialize local call-level toggle.
    useEffect(() => {
        loadPreferences();
    }, []);

    // Keep DOM button class in sync for AudioPlayer scroll behavior
    useEffect(() => {
        document.getElementById('auto-scroll-btn')?.classList.toggle('active', autoScrollEnabled);
    }, [autoScrollEnabled]);

    const getAgentOrCustomerOrText = (phrase) => {
        return phrase.channelID === agentChannelId ? i18n.t('jt_agent.text') : i18n.t('jt_customer.text');
    }

    const playPause = () => {
        if (waveSurfer) {
            const playButtonIcon = document.getElementById('play-button-icon');
            if (waveSurfer.isPlaying()) {
                playButtonIcon?.classList.remove('mdi-pause-circle');
                playButtonIcon?.classList.add('mdi-play-circle');
            } else {
                playButtonIcon?.classList.remove('mdi-play-circle');
                playButtonIcon?.classList.add('mdi-pause-circle');
            }
            waveSurfer.playPause();
        }
    };

    const seekToAndPlay = (t) => {
        if (waveSurfer) {
            if (!waveSurfer.isPlaying()) {
                document
                    .getElementById('play-button-icon')?.classList.remove('bi-play-circle');
                document
                    .getElementById('play-button-icon')?.classList.add('bi-pause-circle');
            }
            waveSurfer.seekTo(t / waveSurfer.getDuration());
            if (!waveSurfer.isPlaying()) {playPause();}
        }
    };

    const persistAutoScrollPreference = async (enabled: boolean) => {
        const key = getAutoScrollPreferenceKey();
        await axios.post(window.URLS.userPreferencesResource, {
            [key]: enabled,
        });
    };

    const changeAutoPlay = async () => {
        const newState = !autoScrollEnabled;
        setAutoScrollEnabled(newState);
        document.getElementById('auto-scroll-btn')?.classList.toggle('active', newState);

        try {
            await persistAutoScrollPreference(newState);
            const key = getAutoScrollPreferenceKey();
            setPreferences((prev) => prev ? {...prev, [key]: newState} : prev);
        } catch (error) {
            setAutoScrollEnabled(!newState);
            document.getElementById('auto-scroll-btn')?.classList.toggle('active', !newState);
            showError(i18n.t('jt_error_fetching_user_preferences.text'));
            console.error('Failed to persist auto-scroll preference', error);
        }
    };

    let transcriptionDownloadItems;
    if (isMono) {
        transcriptionDownloadItems = [
            {
                name: i18n.t('jt_ms_word_docx.text'),
                description: i18n.t('jt_for_quick_reading.text'),
                url: buildURL('getCallTranscriptDocx', {event_id: eventId, speaker: 'all'}),
                disabled: !hasPermission('fs.event.call_transcription_docx.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_text_txt.text'),
                description: i18n.t('jt_for_formal_documents.text'),
                url: buildURL('getCallTranscriptTxt', {event_id: eventId}),
                disabled: !hasPermission('fs.event.call_transcription_txt.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_audio.text'),
                description: i18n.t('jt_for_listening.text'),
                url: buildURL('getCallAudioDownloadMp3', {event_id: eventId}),
                disabled: !hasPermission('fs.event.call_audio.download', userData?.permissions, true) || (preferences?.dataAnonymized && !optionsData?.anonymized_file_exists),
            },
        ]
    } else {
        transcriptionDownloadItems = [
            {
                name: i18n.t('jt_ms_word_all_speakers.text'),
                description: i18n.t('jt_for_quick_reading.text'),
                url: buildURL('getCallTranscriptDocx', {event_id: eventId, speaker: 'all'}),
                disabled: !hasPermission('fs.event.call_transcription_docx.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_ms_word_agent.text'),
                description: i18n.t('jt_for_quick_reading.text'),
                url: buildURL('getCallTranscriptDocx', {event_id: eventId, speaker: 'agent'}),
                disabled: !hasPermission('fs.event.call_transcription_docx.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_ms_word_customer.text'),
                description: i18n.t('jt_for_quick_reading.text'),
                url: buildURL('getCallTranscriptDocx', {event_id: eventId, speaker: 'customer'}),
                disabled: !hasPermission('fs.event.call_transcription_docx.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_text_txt.text'),
                description: i18n.t('jt_for_formal_documents.text'),
                url: buildURL('getCallTranscriptTxt', {event_id: eventId}),
                disabled: !hasPermission('fs.event.call_transcription_txt.download', userData?.permissions, true),
            },
            {
                name: i18n.t('jt_audio.text'),
                description: i18n.t('jt_for_listening.text'),
                url: buildURL('getCallAudioDownloadMp3', {event_id: eventId}),
                disabled: !hasPermission('fs.event.call_audio.download', userData?.permissions, true) || (preferences?.dataAnonymized && !optionsData?.anonymized_file_exists),
            },
        ]
    }

    const handleDownload = async (item: {url: string, name: string}) => {
        if (item.url.includes('download_call_audio')) {
            try {
                const response = await fetch(item.url);
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({message: 'Audio file not available'}));
                    showError(errorData.message || 'Audio file not available');
                    return;
                }
                const blob = await response.blob();
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = item.name;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                showError('Audio file not available');
            }
        } else {
            window.open(item.url, '_blank');
        }
    };

    if (!transcriptAvailable) {
        return <p>{i18n.t('jt_transcript_not_available.text')}</p>;
    }

    return (
        <div>
            {userData?.indexAnonymized && (
                <Badge
                    pill
                    bg="outline-info"
                    className="anonymisation-badge mb-3"
                >
                    {i18n.t('jt_anonymised_conversation.text')}
                </Badge>
            )}

            <div className="mb-2">
                <Icon name="mdi-information-variant" style={{fontSize: '1.3rem'}} className='text-info' />
                <span>{i18n.t('jt_click_the.text')}</span>
            </div>
            <div id="timeline-block">
                <table className={'table'}>
                    <thead className="sticky-header">
                        <tr className="mb-2">
                            <th className="align-middle pb-3" scope={'col'}>{i18n.t('jt_time.text')}</th>
                            {!isMono && (
                                <th className="align-middle pb-3" scope={'col'}>{i18n.t('jt_speaker.text')}</th>
                            )}
                            <th className="align-middle pb-3" scope={'col'}>{i18n.t('jt_text_title.text')}</th>
                            <th className="align-middle pb-3" scope={'col'}>
                                <div className="d-inline-flex justify-content-end align-items-center col-12 p-0">
                                    {transcriptAvailable ?
                                        <>
                                            <Tooltip
                                                content={i18n.t('tt_automated_scrolling.text')}
                                                placement='top'
                                            >
                                                <div className="d-flex align-items-center m-0 mr-4">
                                                    <Form.Check
                                                        id="auto-scroll-btn"
                                                        name="auto-scroll-btn"
                                                        type="switch"
                                                        label={i18n.t('jt_auto_scroll.text')}
                                                        checked={autoScrollEnabled}
                                                        onChange={changeAutoPlay}
                                                        className="mb-0"
                                                    />
                                                </div>
                                            </Tooltip>
                                        </>
                                        : <></>}
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="info"
                                            className="transcript-download-btn"
                                            data-cy="transcript-download-btn"
                                        >
                                            {i18n.t('jt_download.text')}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end">
                                            {transcriptionDownloadItems.map((el, index) =>
                                                <Dropdown.Item
                                                    key={index}
                                                    className={el.disabled ? 'disabled' : ''}
                                                    onClick={() => !el.disabled && handleDownload(el)}
                                                >
                                                    {el.name}
                                                </Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {timelineBlock.map((phrase, scrollID) => (
                            <tr
                                key={'Scroll-block-' + scrollID}
                                className={'col-12 phrase'}
                                id={'Scroll-block-' + scrollID}
                            >
                                {phrase.channelID % 2 === 0 ? (
                                    <>
                                        <td key={'A' + phrase.channelID}
                                            className={'red left'}>
                                            <span
                                                key={'DA' + phrase.channelID}
                                                className='clickable-word'
                                                onClick={() => seekToAndPlay(phrase.startTime)}
                                            >
                                                {`${phrase['mm:ss']} `}
                                            </span>
                                        </td>
                                        {!isMono && (
                                            <td key={'B' + phrase.channelID}
                                                className={'red middle'}>
                                                {getAgentOrCustomerOrText(phrase)}
                                            </td>
                                        )}
                                        <td colSpan={2} key={'C' + phrase.channelID}
                                            className={'red right'}>
                                            {phrase.alternatives[0].words.map((word, index) => (
                                                <span
                                                    key={'D' + phrase.channelID + '' + index}
                                                    className='clickable-word'
                                                    id={'timelineBlock-word-' + word.word_id}
                                                    onClick={() => seekToAndPlay(word.startTime)}
                                                >
                                                    {`${word.word} `}
                                                </span>
                                            ))}
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td key={'A' + phrase.channelID}
                                            className={'blue left'}>
                                            <span
                                                key={'DA' + phrase.channelID}
                                                className='clickable-word'
                                                onClick={() => seekToAndPlay(phrase.startTime)}
                                            >
                                                {`${phrase['mm:ss']} `}
                                            </span>
                                        </td>
                                        {!isMono && (
                                            <td key={'B' + phrase.channelID}
                                                className={'blue middle'}>
                                                {getAgentOrCustomerOrText(phrase)}
                                            </td>
                                        )}
                                        <td colSpan={2}
                                            key={'C' + phrase.channelID}
                                            className={'blue right'}
                                        >
                                            {phrase.alternatives[0].words.map((word, index) => (
                                                <span
                                                    key={'D' + phrase.channelID + '' + index}
                                                    className='clickable-word'
                                                    id={'timelineBlock-word-' + word.word_id}
                                                    onClick={() => seekToAndPlay(word.startTime)}
                                                >
                                                    {`${word.word} `}
                                                </span>
                                            ))}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
})
    ;

export default CallTranscript;